import type { IdTokenClaims } from "./types"
import { base64UrlDecode, base64UrlToArrayBuffer } from "./crypto"

interface JWK {
  kty: string
  kid?: string
  use?: string
  n?: string
  e?: string
  alg?: string
  [key: string]: unknown
}

interface JWKS {
  keys: JWK[]
}

let jwksCache: JWKS | null = null
let jwksCacheUri: string | null = null
let jwksCacheTime = 0
const JWKS_CACHE_TTL = 60 * 60 * 1000 // 1 hour

export async function fetchJWKS(jwksUri: string): Promise<JWKS> {
  if (jwksCache && jwksCacheUri === jwksUri && Date.now() - jwksCacheTime < JWKS_CACHE_TTL) {
    return jwksCache
  }

  const res = await fetch(jwksUri)
  if (!res.ok) {
    throw new Error(`Failed to fetch JWKS: ${res.status} ${res.statusText}`)
  }

  jwksCache = await res.json()
  jwksCacheUri = jwksUri
  jwksCacheTime = Date.now()
  return jwksCache!
}

export async function refreshJWKS(jwksUri: string): Promise<JWKS> {
  jwksCache = null
  jwksCacheUri = null
  jwksCacheTime = 0
  return fetchJWKS(jwksUri)
}

function findKey(jwks: JWKS, kid: string): JWK | undefined {
  return jwks.keys.find((k) => k.kid === kid && k.kty === "RSA" && (k.use === "sig" || !k.use))
}

export async function verifyIdToken(
  idToken: string,
  jwksUri: string,
  expectedIssuer: string,
  expectedAudience: string,
  expectedNonce?: string
): Promise<IdTokenClaims> {
  const parts = idToken.split(".")
  if (parts.length !== 3) {
    throw new Error("Invalid ID token format")
  }

  const [headerB64, payloadB64, signatureB64] = parts

  const header = JSON.parse(base64UrlDecode(headerB64))
  if (header.alg !== "RS256") {
    throw new Error(`Unsupported ID token algorithm: ${header.alg}`)
  }

  const kid = header.kid
  if (!kid) {
    throw new Error("ID token header missing kid")
  }

  let jwks = await fetchJWKS(jwksUri)
  let jwk = findKey(jwks, kid)

  // Key rotation: if kid not found, refresh JWKS once
  if (!jwk) {
    jwks = await refreshJWKS(jwksUri)
    jwk = findKey(jwks, kid)
    if (!jwk) {
      throw new Error(`No matching key found in JWKS for kid: ${kid}`)
    }
  }

  const cryptoKey = await crypto.subtle.importKey(
    "jwk",
    { kty: jwk.kty, n: jwk.n, e: jwk.e },
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    false,
    ["verify"]
  )

  const signedData = new TextEncoder().encode(`${headerB64}.${payloadB64}`)
  const signature = base64UrlToArrayBuffer(signatureB64)

  const valid = await crypto.subtle.verify("RSASSA-PKCS1-v1_5", cryptoKey, signature, signedData)
  if (!valid) {
    throw new Error("ID token signature verification failed")
  }

  const payload: IdTokenClaims = JSON.parse(base64UrlDecode(payloadB64))

  // Validate claims
  if (payload.iss !== expectedIssuer) {
    throw new Error(`ID token issuer mismatch: expected ${expectedIssuer}, got ${payload.iss}`)
  }

  const aud = Array.isArray(payload.aud) ? payload.aud : [payload.aud]
  if (!aud.includes(expectedAudience)) {
    throw new Error(`ID token audience mismatch: expected ${expectedAudience}`)
  }

  const now = Math.floor(Date.now() / 1000)
  if (typeof payload.exp === "number" && now > payload.exp + 60) {
    throw new Error("ID token has expired")
  }

  if (typeof payload.iat === "number" && payload.iat > now + 60) {
    throw new Error("ID token issued in the future")
  }

  if (expectedNonce && payload.nonce !== expectedNonce) {
    throw new Error("ID token nonce mismatch")
  }

  return payload
}
